/**
 * Browser Wallet Modal
 *
 * Main modal for wallet connection, creation, and management
 * Enhanced with bank-grade UI design
 */

import { Lock, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBrowserWallet } from "@nullaone/wallet-react";

import { WalletSelectStep } from "./WalletSelectStep";
import { CreateWalletForm } from "./CreateWalletForm";
import { ImportWalletForm } from "./ImportWalletForm";
import { LedgerConnectForm } from "./LedgerConnectForm";
import { MnemonicDisplay } from "./MnemonicDisplay";
import { BackupWarning } from "./BackupWarning";

export function BrowserWalletModal() {
  const { t } = useTranslation();
  const { isModalOpen, closeModal, modalStep, error } = useBrowserWallet();

  const getTitle = () => {
    switch (modalStep) {
      case "select":
        return t("wallet.modal.titles.select");
      case "create":
        return t("wallet.modal.titles.create");
      case "import":
        return t("wallet.modal.titles.import");
      case "ledger":
        return t("wallet.modal.titles.ledger");
      case "mnemonic":
        return t("wallet.modal.titles.mnemonic");
      case "backup":
        return t("wallet.modal.titles.backup");
      default:
        return t("wallet.modal.titles.default");
    }
  };

  const getDescription = () => {
    switch (modalStep) {
      case "select":
        return t("wallet.modal.descriptions.select");
      case "create":
        return t("wallet.modal.descriptions.create");
      case "import":
        return t("wallet.modal.descriptions.import");
      case "ledger":
        return t("wallet.modal.descriptions.ledger");
      case "mnemonic":
        return t("wallet.modal.descriptions.mnemonic");
      case "backup":
        return t("wallet.modal.descriptions.backup");
      default:
        return "";
    }
  };

  const renderStep = () => {
    switch (modalStep) {
      case "select":
        return <WalletSelectStep />;
      case "create":
        return <CreateWalletForm />;
      case "import":
        return <ImportWalletForm />;
      case "ledger":
        return <LedgerConnectForm />;
      case "mnemonic":
        return <MnemonicDisplay />;
      case "backup":
        return <BackupWarning />;
      default:
        return <WalletSelectStep />;
    }
  };

  // Check if this is a security-focused step (create, import, mnemonic, backup)
  const isSecurityStep = ["create", "import", "mnemonic", "backup"].includes(
    modalStep,
  );

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-md border-border-card bg-white">
        {/* Security indicator stripe */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-primary to-brand-primary-light rounded-t-lg" />

        <DialogHeader className="relative">
          {/* Security badge for sensitive operations */}
          {isSecurityStep && (
            <div className="absolute -top-1 right-0">
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-brand-primary/8 border border-brand-primary/20">
                <Lock className="h-3 w-3 text-brand-primary" />
                <span className="text-[11px] text-brand-primary font-semibold uppercase tracking-wider">
                  {t("wallet.modal.secureBadge")}
                </span>
              </div>
            </div>
          )}

          <DialogTitle className="text-text-primary flex items-center gap-2">
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-text-muted">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-lg bg-danger/10 border border-danger/30 p-3 text-sm text-danger">
            {error}
          </div>
        )}

        {renderStep()}

        {/* Security footer */}
        <div className="flex items-center justify-center gap-2 pt-2 border-t border-border-card">
          <Shield className="h-3.5 w-3.5 text-brand-primary/60" />
          <span className="text-xs text-text-muted">
            {t("wallet.modal.protectedFooter")}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
